import { isSessionActive } from './sessionActivity';
import { hlsXhrWithCredentials } from './liveStreamAuth';

export const LIVE_LAG_THRESHOLD_SEC = 3;
export const PLAY_SYNC_MIN_LAG_SEC = 3;
export const CATCHUP_CHECK_INTERVAL_MS = 2000;
export const HARD_JUMP_LAG_SEC = 10;
export const CATCHUP_SEEK_COOLDOWN_MS = 5000;

/**
 * Opzioni catch-up per telefono: sul mobile il 1.5x spesso non recupera
 * il ritardo che si accumula, quindi si salta al live un po' prima (6s)
 * rispetto al PC (10s). Niente liveMaxLatencyDurationCount (aveva peggiorato tutto).
 */
export const MOBILE_CATCHUP_OPTIONS = {
  thresholdSec: 5,
  hardJumpLagSec: 6,
  cooldownMs: 5000,
  intervalMs: 2000,
};

/** Config hls.js: accelerazione live + cookie stream_t. */
export const HLS_LIVE_CONFIG = {
  maxLiveSyncPlaybackRate: 1.5,
  xhrSetup: hlsXhrWithCredentials,
};

export function getBufferedEnd(video) {
  if (!video?.buffered || video.buffered.length === 0) {
    return null;
  }
  return video.buffered.end(video.buffered.length - 1);
}

/**
 * Edge live reale. Non usare buffered.end: misura il ritardo in modo sbagliato
 * (sembra piccolo mentre in realtà cresce).
 */
export function getLiveEdge(video, hls) {
  if (hls && Number.isFinite(hls.liveSyncPosition)) {
    return hls.liveSyncPosition;
  }

  if (video?.seekable && video.seekable.length > 0) {
    return video.seekable.end(video.seekable.length - 1);
  }

  return null;
}

export function getLiveLagSeconds(video, hls) {
  if (!video) {
    return 0;
  }
  const liveEdge = getLiveEdge(video, hls);
  if (!Number.isFinite(liveEdge)) {
    return 0;
  }
  return liveEdge - video.currentTime;
}

/**
 * Seek al live se il lag supera la soglia.
 * - lag moderato (< hardJump): se il live è già in buffer, vai lì;
 *   altrimenti lascia fare a maxLiveSyncPlaybackRate (niente seek rischioso).
 * - lag alto (>= hardJump): salto a liveSyncPosition / seekable.end.
 */
export function syncToLiveEdgeIfNeeded(video, hls, options = {}) {
  const minLagSec = options.minLagSec ?? PLAY_SYNC_MIN_LAG_SEC;
  const hardJumpLagSec = options.hardJumpLagSec ?? HARD_JUMP_LAG_SEC;

  if (!video || video.seeking) {
    return false;
  }

  const liveEdge = getLiveEdge(video, hls);
  if (!Number.isFinite(liveEdge)) {
    return false;
  }

  const lag = liveEdge - video.currentTime;
  if (lag < minLagSec) {
    return false;
  }

  let target = liveEdge;

  if (lag < hardJumpLagSec) {
    const bufferedEnd = getBufferedEnd(video);
    if (!Number.isFinite(bufferedEnd)) {
      return false;
    }
    // Live già scaricato → piccolo seek sicuro. Altrimenti solo rate 1.5x.
    if (liveEdge <= bufferedEnd + 0.35) {
      target = liveEdge;
    } else {
      return false;
    }
  }

  if (!Number.isFinite(target) || target - video.currentTime < 0.25) {
    return false;
  }

  try {
    video.currentTime = target;
  } catch (_) {
    return false;
  }
  return true;
}

function resumeAfterSeekIfNeeded(video) {
  if (!video) {
    return;
  }
  const onSeeked = () => {
    video.removeEventListener('seeked', onSeeked);
    if (video.paused && !document.hidden) {
      video.play().catch(() => {});
    }
  };
  video.addEventListener('seeked', onSeeked);
  // Fallback se seeked non arriva
  setTimeout(() => {
    video.removeEventListener('seeked', onSeeked);
    if (video.paused && !document.hidden) {
      video.play().catch(() => {});
    }
  }, 1500);
}

/**
 * Su play / rientro tab: seek solo se davvero indietro (evita abort del play).
 */
export function schedulePlayLiveSync(video, getHls, options = {}) {
  const minLagSec = options.minLagSec ?? options.thresholdSec ?? PLAY_SYNC_MIN_LAG_SEC;
  const hardJumpLagSec = options.hardJumpLagSec ?? HARD_JUMP_LAG_SEC;

  if (!video) {
    return () => {};
  }

  const timeoutId = setTimeout(() => {
    const hls = typeof getHls === 'function' ? getHls() : null;
    const lag = getLiveLagSeconds(video, hls);

    if (lag < minLagSec) {
      return;
    }

    const didSeek = syncToLiveEdgeIfNeeded(video, hls, { minLagSec, hardJumpLagSec });
    if (!didSeek) {
      if (video.paused) {
        video.play().catch(() => {});
      }
      return;
    }

    resumeAfterSeekIfNeeded(video);
    video.play().catch(() => {});
  }, 0);

  return () => {
    clearTimeout(timeoutId);
  };
}

export function maybeCatchUpToLive(video, hls, options = {}) {
  const thresholdSec = options.minLagSec ?? options.thresholdSec ?? LIVE_LAG_THRESHOLD_SEC;
  const hardJumpLagSec = options.hardJumpLagSec ?? HARD_JUMP_LAG_SEC;

  if (!video || video.paused || video.seeking) {
    return false;
  }

  const didSeek = syncToLiveEdgeIfNeeded(video, hls, {
    minLagSec: thresholdSec,
    hardJumpLagSec,
  });

  if (didSeek) {
    resumeAfterSeekIfNeeded(video);
  }

  return didSeek;
}

export function startLiveCatchupMonitor(video, getHls, options = {}) {
  if (!video) {
    return () => {};
  }

  const intervalMs = options.intervalMs ?? CATCHUP_CHECK_INTERVAL_MS;
  const thresholdSec = options.thresholdSec ?? LIVE_LAG_THRESHOLD_SEC;
  const hardJumpLagSec = options.hardJumpLagSec ?? HARD_JUMP_LAG_SEC;
  const cooldownMs = options.cooldownMs ?? CATCHUP_SEEK_COOLDOWN_MS;
  let lastSeekAt = 0;

  const intervalId = setInterval(() => {
    if (!isSessionActive()) {
      return;
    }

    const now = Date.now();
    if (now - lastSeekAt < cooldownMs) {
      return;
    }

    const hls = typeof getHls === 'function' ? getHls() : null;
    if (maybeCatchUpToLive(video, hls, { thresholdSec, hardJumpLagSec })) {
      lastSeekAt = now;
    }
  }, intervalMs);

  return () => {
    clearInterval(intervalId);
  };
}
